<template>
  <nav :class="$style.container">
    <section
      v-if="
        viewer && avatarImageUrl && reviewScore != null && shadowProfilePageUrl
      "
      :class="$style.profilePanel"
    >
      <img :src="avatarImageUrl" alt="" :class="$style.avatar" />
      <div>
        <p :class="$style.profileName">{{ viewer.profile.name }}</p>
        <RatingStars :rate="reviewScore" :class="$style.profilePanel__stars" />
        <div :class="$style.externalLinkWrapper">
          <ExternalLink
            :href="shadowProfilePageUrl"
            :class="$style.externalLink"
            >プロフィールページを確認</ExternalLink
          >
          <ExternalLink
            v-if="myStoryUrl"
            :href="myStoryUrl"
            :class="$style.externalLink"
            >マイストーリーを確認</ExternalLink
          >
        </div>
      </div>
    </section>
    <section
      v-if="plannerLinks.length > 0"
      :class="[$style.menu, $style.container__menu]"
    >
      <ul :class="[$style.menu__list, $style.list]">
        <li
          v-for="link in plannerLinks"
          :key="link.text"
          :class="$style.list__item"
        >
          <RouterLink
            :to="link.to"
            :class="$style.link"
            :active-class="$style['link--active']"
          >
            {{ link.text }}
            <IconAngle />
          </RouterLink>
        </li>
      </ul>
    </section>
    <section :class="[$style.menu, $style.container__menu]">
      <h2 :class="[$style.title, $style.menu__title]">設定</h2>
      <ul :class="[$style.menu__list, $style.list]">
        <li
          v-for="link in settingLinks"
          :key="link.text"
          :class="$style.list__item"
        >
          <RouterLink
            :to="link.to"
            :class="$style.link"
            :active-class="$style['link--active']"
          >
            {{ link.text }}
            <IconAngle />
          </RouterLink>
        </li>
      </ul>
    </section>
    <SettingsPageSideMenuLineActivation
      v-if="props.viewer?.hasLineActivationProvider"
      :class="$style.container__menu"
    />
    <SettingsPageSideMenuSub :class="$style.container__menu" />
  </nav>
</template>

<script setup lang="ts">
import IconAngle from '@/components/icons/IconAngle.vue'
import SettingsPageSideMenuLineActivation from './SettingsPageSideMenuLineActivation.vue'
import SettingsPageSideMenuSub from './SettingsPageSideMenuSub.vue'
import type {
  SettingsPageSideMenuContractFragment,
  SettingsPageSideMenuViewerFragment,
} from './SettingsPageSideMenu.generated'
import type { InternalLinkType } from '@/types/app'
import { computed } from 'vue'
import { createAvatarFromImgixPath } from '@/apis/repositories/parsers/plannerAvatar'
import RatingStars from '@/components/molecules/RatingStars.vue'
import { getShadowPlannerDetailURL } from '@/config'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import type { SettingsPageUiControlFragment } from './SettingsPage.generated'
import { useTestMode } from '@/hooks/useTestMode'

const props = defineProps<{
  contract?: SettingsPageSideMenuContractFragment
  viewer?: SettingsPageSideMenuViewerFragment
  uiControl?: SettingsPageUiControlFragment
}>()

type ConditionalLink = InternalLinkType & {
  condition: boolean
}
const { enabled } = useTestMode()

const plannerLinks = computed(() => {
  const conditionalLinks: ConditionalLink[] = [
    {
      text: '予約一覧',
      to: {
        name: 'EntranceBookingsPage',
      },
      exact: false,
      condition: enabled.value.alpha,
    },
  ]

  return conditionalLinks.filter(link => link.condition)
})

const settingLinks = computed(() => {
  const conditionalLinks: ConditionalLink[] = [
    {
      text: 'プロフィール',
      to: {
        name: 'plannerSettings',
      },
      condition: true,
    },
    {
      text: 'アカウント',
      to: {
        name: 'accountSettings',
      },
      condition: true,
    },
    {
      text: 'メンバー管理',
      to: {
        name: 'memberManagementSetting',
      },
      condition: props.uiControl?.editStaffs ?? false,
    },
    {
      text: '利用状況',
      to: {
        name: 'usageSettings',
      },
      condition: true,
    },
    {
      text: '企業情報',
      to: {
        name: 'corporationSettings',
      },
      condition: props.uiControl?.editCorporation ?? false,
    },
    {
      text: '自動アプローチ',
      to: {
        name: 'autoPostKartePlannerComment',
      },
      condition:
        !!props.viewer && props.viewer.autoPostKartePlannerComment.available,
    },
    {
      text: '自動応答チャットメッセージ',
      to: {
        name: 'autoChatReplySetting',
      },
      condition: true,
    },
    {
      text: 'シナリオチャット',
      to: {
        name: 'scenarioChatIndex',
      },
      condition: props.uiControl?.useLineMessageFeatures ?? false,
    },
    {
      text: 'テンプレート',
      to: {
        name: 'chatMessageTemplate',
      },
      condition: true,
    },
    {
      text: '初期チャットメッセージ',
      to: {
        name: 'introductionMessageSetting',
      },
      condition: true,
    },
    {
      text: 'カレンダー連携設定',
      to: {
        name: 'calendarSetting',
      },
      condition: true,
    },
    {
      text: 'アプローチ自動代行',
      to: {
        name: 'attackListSenderSetting',
      },
      condition: props.viewer?.attackListSender.available ?? false,
    },
  ]

  return conditionalLinks.filter(link => link.condition)
})

const reviewScore = computed(() => {
  if (!props.viewer) return null
  return props.viewer.profile.reviewScore
})

const avatarImageUrl = computed(() => {
  if (!props.viewer?.profile.avatar) return null
  return createAvatarFromImgixPath(props.viewer.profile.avatar)
    .size({ width: 96, height: 96 })
    .toFacefitUrlString()
})

const shadowProfilePageUrl = computed(() => {
  if (!props.viewer) return null

  const plannerId = Number(props.viewer.id)
  return getShadowPlannerDetailURL(plannerId)
})

const myStoryUrl = computed(() => {
  return props.viewer?.myStoryUrl
})
</script>

<style lang="scss" module>
.container {
  &__menu {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.profilePanel {
  display: flex;
  gap: 16px;
  background-color: $color-surface-primary;
  border-radius: 12px;
  box-shadow: $shadow-4;
  padding: 24px;

  &__stars {
    margin-top: 4px;
  }
}

.avatar {
  height: 48px;
  width: 48px;
  border-radius: 24px;
}

.profileName {
  font-size: 20px;
  font-weight: 700;
}

.menu {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-4;
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin: 20px 16px;
}

.list {
  &__item {
    border-top: 1px solid $color-border-low-emphasis;
  }
}

.link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding: 20px 16px;
  color: $color-text-and-object-high-emphasis;

  &:hover {
    text-decoration: none;
    color: $color-text-and-object-accent-primary;
  }

  &--active {
    background-color: $color-surface-accent-primary-light;
    color: $color-text-and-object-accent-primary;
    font-weight: 700;
  }
}

.externalLinkWrapper {
  margin-top: 8px;

  > *:nth-child(n + 2) {
    margin-top: 8px;
  }
}

.externalLink {
  color: $color-text-and-object-accent-primary;
  font-size: 12px;
  text-decoration: underline;
}
</style>
